import { useState, useEffect, useRef, useId } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Menu, X, ChevronDown } from 'lucide-react';
import { RACINE_ZONES, communeUrl, communesDuMenu } from '../data/communes';
import { LIBELLE_LIFT } from '../data/navigation';
import { mesurerEstimationPhotos } from '../lib/mesure';

interface DropdownItem {
  label: string;
  to: string;
  badge?: string;
  /** Renseigné sur les entrées dont le clic est une conversion à mesurer. */
  mesure?: () => void;
}

interface NavItem {
  label: string;
  /** Toujours renseigné : une entrée de menu qui ne navigue pas n'en est pas une. */
  to: string;
  children?: DropdownItem[];
}

/**
 * LE header du site — un seul composant, rendu à l'identique sur toutes les
 * pages, sous-menus compris.
 *
 * Lot 16. Il y avait deux headers : celui-ci sur l'accueil, avec ses
 * sous-menus, et `ServiceNavbar` sur les quatre-vingt-dix-sept autres pages,
 * réduit à six liens. Sur ces pages, rien ne menait au monte-meubles, à
 * l'estimateur, à la page des zones ni à aucune commune. Un lien de header est
 * répété sur chaque page du site : c'est le levier de maillage le plus
 * puissant qui existe ici, et il ne portait que sur une page.
 *
 * Trois règles, qui ne bougent pas :
 *   · les sous-menus sont de vrais <a href> présents dans le HTML pré-rendu,
 *     pas générés au survol — un robot qui n'exécute pas de JS les voit ;
 *   · le header n'est PAS contextuel : c'est la navigation globale, identique
 *     partout, et le contrôle de build vérifie qu'elle l'est ;
 *   · plus aucun lien vers une ancre `/#…` : « Contact » mène à /contact-devis
 *     depuis la fusion des deux pages (crawl du 07/09/2026).
 *
 * Ce qui vient des données : le menu « Zones » lit les communes cochées
 * `featured_nav` en base, dans leur `nav_order` ; le libellé de l'entrée
 * monte-meubles vient de la table `navigation` (« Location de lift » dès que
 * le lot 5 sera arbitré).
 *
 * Accessibilité : chaque parent est un lien qui navigue, doublé d'un bouton
 * qui déplie (`aria-expanded`, `aria-controls`). Le sous-menu s'ouvre au
 * survol, au focus clavier (`focus-within`) et au bouton ; Échap le referme.
 */
function construireMenu(): NavItem[] {
  return [
    {
      label: 'Déménagement',
      to: '/demenagement',
      children: [
        { label: 'Déménagement à Liège', to: '/demenagement/demenagement-liege' },
        { label: "Déménagement d'entreprise", to: '/demenagement/demenagement-entreprise' },
        { label: 'Déménagement international', to: '/demenagement/demenagement-international' },
        { label: 'Déménagement de piano', to: '/demenagement/demenagement-piano' },
        { label: 'Démontage et remontage', to: '/demenagement/demontage-remontage-meubles' },
        {
          label: 'Estimer mon volume',
          to: '/estimation-volume',
          badge: 'NOUVEAU',
          mesure: () => mesurerEstimationPhotos('navigation'),
        },
      ],
    },
    {
      label: 'Garde-meubles',
      to: '/garde-meubles',
      children: [
        { label: 'Garde-meubles à Liège', to: '/garde-meubles/garde-meubles-liege' },
        { label: "Prix d'un garde-meubles", to: '/garde-meubles/prix-garde-meubles-liege' },
      ],
    },
    { label: LIBELLE_LIFT, to: '/monte-meubles' },
    {
      label: 'Zones',
      to: RACINE_ZONES,
      children: [
        ...communesDuMenu().map((c) => ({ label: c.nom, to: communeUrl(c) })),
        { label: 'Toutes les communes', to: RACINE_ZONES },
      ],
    },
    { label: 'Blog', to: '/blog' },
    { label: 'Contact', to: '/contact-devis' },
  ];
}

/** Construit une fois : les données ne changent pas pendant la vie de la page. */
const NAV_ITEMS: NavItem[] = construireMenu();

function DesktopItem({ item, scrolled }: { item: NavItem; scrolled: boolean }) {
  const [ouvert, setOuvert] = useState(false);
  const idMenu = useId();
  const conteneur = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ouvert) return;
    const fermerHorsClic = (e: MouseEvent) => {
      if (!conteneur.current?.contains(e.target as Node)) setOuvert(false);
    };
    document.addEventListener('mousedown', fermerHorsClic);
    return () => document.removeEventListener('mousedown', fermerHorsClic);
  }, [ouvert]);

  const couleurLien = scrolled ? 'text-white hover:text-yellow' : 'text-navy';

  if (!item.children) {
    return (
      <Link to={item.to} className={`relative text-[15px] font-medium transition-colors py-2 ${couleurLien}`}>
        {item.label}
      </Link>
    );
  }

  return (
    <div
      ref={conteneur}
      className="relative group"
      onKeyDown={(e) => { if (e.key === 'Escape') setOuvert(false); }}
      onBlur={(e) => { if (!conteneur.current?.contains(e.relatedTarget as Node)) setOuvert(false); }}
    >
      <div className={`flex items-center gap-0.5 ${couleurLien}`}>
        <Link to={item.to} className="text-[15px] font-medium transition-colors py-2">
          {item.label}
        </Link>
        {/* Le lien navigue, le bouton déplie : deux actions, deux éléments.
            Le chevron signale le sous-menu ; sans lui, un item qui navigue ET
            déroule paraît n'être qu'un lien simple. */}
        <button
          type="button"
          aria-expanded={ouvert}
          aria-controls={idMenu}
          aria-label={`Afficher les pages de la section ${item.label}`}
          onClick={() => setOuvert((o) => !o)}
          className="p-1 rounded transition-colors hover:text-yellow focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow"
        >
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${ouvert ? 'rotate-180' : ''}`} aria-hidden="true" />
        </button>
      </div>
      {/* Toujours dans le DOM : masqué par CSS, révélé au survol, au focus
          clavier ou à l'ouverture explicite. */}
      <ul
        id={idMenu}
        className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-60 rounded-xl shadow-xl border py-2 transition-all duration-200 z-50 group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible ${
          ouvert ? 'opacity-100 visible' : 'opacity-0 invisible'
        } ${scrolled ? 'bg-[#0D1B5E] border-white/10' : 'bg-white border-gray-100'}`}
      >
        {item.children.map((enfant) => (
          <li key={enfant.to + enfant.label}>
            <Link
              to={enfant.to}
              onClick={() => { enfant.mesure?.(); setOuvert(false); }}
              className={`block px-4 py-3 text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-yellow ${
                scrolled ? 'text-white/70 hover:text-yellow hover:bg-white/5' : 'text-navy/70 hover:text-navy hover:bg-offwhite'
              }`}
            >
              {enfant.label}
              {enfant.badge && (
                <span className="ml-2 bg-[#F0B800] text-[#132073] text-[10px] font-bold rounded-full px-2 py-0.5 align-middle">{enfant.badge}</span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMobileDropdown, setOpenMobileDropdown] = useState<string | null>(null);
  const idMobile = useId();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggleMobileDropdown = (label: string) => {
    setOpenMobileDropdown(openMobileDropdown === label ? null : label);
  };

  const fermerMobile = () => { setMobileOpen(false); setOpenMobileDropdown(null); };

  return (
    <header
      id="navigation-principale"
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        scrolled ? 'bg-navy shadow-lg' : 'bg-white shadow-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8 flex items-center justify-between h-16 md:h-[72px]">
        {/* Le logo est LE lien vers l'accueil — il n'y a plus d'entrée
            « Accueil » dans le menu, et jamais d'ancre `/#accueil`. */}
        <Link to="/" onClick={fermerMobile} className="flex items-center p-1 -m-1" aria-label="Déménagements Gramme — Accueil">
          {scrolled ? (
            <span className="flex items-center gap-1.5">
              <span className="font-black text-[15px] uppercase tracking-tight leading-none text-white">Déménagements</span>
              <span className="font-black text-[15px] uppercase tracking-tight leading-none text-yellow">Gramme</span>
            </span>
          ) : (
            <picture>
              <source srcSet="/logo-gramme-300.webp" type="image/webp" />
              <img
                src="/logo-gramme-300.png"
                alt="Déménagements Gramme"
                className="h-[56px] md:h-[70px] w-auto"
                loading="eager"
                width="300"
                height="224"
              />
            </picture>
          )}
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Navigation principale">
          {NAV_ITEMS.map((item) => (
            <DesktopItem key={item.label} item={item} scrolled={scrolled} />
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <Link
            to="/contact-devis"
            className="hidden md:flex items-center gap-1.5 px-4 py-2 rounded font-bold text-sm uppercase tracking-wide transition-all duration-200 bg-[#F0B800] text-[#0D1020] border border-[#C89A00] hover:bg-[#EAB000]"
          >
            Devis Gratuit
            <ArrowRight className="w-4 h-4" />
          </Link>

          <button
            // p-3 porte la zone de touche de 24 à 48 px, le minimum recommandé
            // pour une cible tactile ; -mr-3 compense le padding pour que
            // l'icône reste au même endroit à l'écran.
            type="button"
            className={`lg:hidden p-3 -mr-3 transition-colors ${scrolled ? 'text-white' : 'text-navy'}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
            aria-expanded={mobileOpen}
            aria-controls={idMobile}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Menu mobile : dans le DOM dès le pré-rendu, masqué tant qu'il est
          fermé. Il n'était monté qu'après hydratation, au clic : le HTML
          livré n'en contenait rien. Le contenu est le même que le menu de
          bureau — un seul jeu de liens, deux présentations.

          ⚠️ L'AFFICHAGE EST PILOTÉ PAR LA CLASSE, PAS PAR L'ATTRIBUT `hidden`.
          ────────────────────────────────────────────────────────────────────
          `hidden` seul ne masquait RIEN ici, et le menu est resté ouvert en
          production sur les 121 pages — repéré le 07/09/2026 sur la capture
          PageSpeed, reproduit en 360 px comme en 412 px : le H1 de l'accueil
          se retrouvait à 712 px du haut, donc sous la ligne de flottaison sur
          un téléphone, derrière un menu que personne n'avait ouvert.

          La cause tient à la cascade. L'attribut s'appuie sur la règle du
          navigateur `[hidden] { display: none }`, dont la spécificité est
          nulle ; la classe utilitaire `flex` pose `display: flex` et l'emporte
          systématiquement. Le piège vaut pour toute classe d'affichage —
          `flex`, `grid`, `block`, `inline-flex`.

          `hidden` est CONSERVÉ malgré tout : il porte le sens, et il reste le
          seul signal correct si une feuille de style venait à manquer. Mais
          c'est `hidden`/`flex` qui décide réellement.

          Le <ul> des sous-menus (plus bas) n'est PAS concerné : il ne porte
          aucune classe d'affichage, donc la règle du navigateur s'y applique
          normalement. */}
      <nav
        id={idMobile}
        hidden={!mobileOpen}
        aria-label="Navigation principale (mobile)"
        className={`lg:hidden bg-navy px-8 py-6 flex-col gap-2 ${mobileOpen ? 'flex' : 'hidden'}`}
      >
        {NAV_ITEMS.map((item) => {
          const ouvert = openMobileDropdown === item.label;
          const idSousMenu = `${idMobile}-${item.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
          return (
            <div key={item.label}>
              {item.children ? (
                /* Item qui navigue ET déroule. Sur mobile il n'y a pas de
                   survol : sans ce chevron séparé, ouvrir le sous-menu serait
                   impossible et ses liens deviendraient inatteignables. Le
                   libellé navigue, le chevron déroule. */
                <div className="flex items-center">
                  <Link
                    to={item.to}
                    onClick={fermerMobile}
                    className="flex-1 text-yellow text-lg font-bold uppercase text-left px-4 py-3 rounded transition-colors duration-200 hover:bg-[#F0B800] hover:text-[#0D1020]"
                  >
                    {item.label}
                  </Link>
                  <button
                    type="button"
                    onClick={() => toggleMobileDropdown(item.label)}
                    aria-label={`Afficher les pages de la section ${item.label}`}
                    aria-expanded={ouvert}
                    aria-controls={idSousMenu}
                    className="text-yellow px-4 py-3 rounded transition-colors duration-200 hover:bg-[#F0B800] hover:text-[#0D1020]"
                  >
                    <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${ouvert ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </button>
                </div>
              ) : (
                <Link
                  to={item.to}
                  onClick={fermerMobile}
                  className="text-yellow text-lg font-bold uppercase text-left px-4 py-3 rounded transition-colors duration-200 hover:bg-[#F0B800] hover:text-[#0D1020] block"
                >
                  {item.label}
                </Link>
              )}
              {item.children && (
                <ul id={idSousMenu} hidden={!ouvert} className="ml-4 mt-1 mb-2 space-y-1">
                  {item.children.map((enfant) => (
                    <li key={enfant.to + enfant.label}>
                      <Link
                        to={enfant.to}
                        onClick={() => { enfant.mesure?.(); fermerMobile(); }}
                        className="block text-white/70 text-base font-medium px-4 py-3 rounded hover:text-yellow hover:bg-white/5 transition-colors"
                      >
                        {enfant.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
        <Link
          to="/contact-devis"
          onClick={fermerMobile}
          className="mt-2 bg-[#F0B800] text-[#0D1020] font-bold uppercase py-3 px-6 rounded flex items-center gap-2 justify-center border border-[#C89A00]"
        >
          Devis Gratuit
          <ArrowRight className="w-4 h-4" />
        </Link>
      </nav>
    </header>
  );
}
